import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { num: '01', label: 'Home',        href: '/' },
  { num: '02', label: 'The person',  href: '/the-person' },
  { num: '03', label: 'Projects',    href: '/projects' },
  { num: '04', label: 'Travel',      href: '/travel' },
  { num: '05', label: 'Career & Ed', href: '/career' },
  { num: '06', label: 'Blog',        href: '/blog' },
  { num: '07', label: 'Connect',     href: '/connect' },
];

export default function Sidebar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Helper: is this nav item active?
  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const SidebarContent = () => (
    <div className="sidebar" style={{ position: isMobile ? 'fixed' : 'fixed' }}>
      {/* Logo / brand */}
      <div style={{ padding: '32px 24px 24px' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1.6rem',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: 'var(--text)',
            lineHeight: 1,
          }}>
            MT
          </div>
          <div style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--text-3)',
            marginTop: '4px',
          }}>
            massimo terzi
          </div>
        </Link>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border)', margin: '0 16px' }} />

      {/* Nav items */}
      <nav style={{ padding: '16px 0', flex: 1 }}>
        {NAV_ITEMS.map(({ num, label, href }) => {
          const active = isActive(href);
          return (
            <Link
              key={href}
              to={href}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '9px 20px',
                textDecoration: 'none',
                borderRadius: '0 6px 6px 0',
                marginRight: '12px',
                marginBottom: '2px',
                background: active ? 'var(--surface-2)' : 'transparent',
                borderLeft: active ? '2px solid var(--gold)' : '2px solid transparent',
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = 'var(--surface-2)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.65rem',
                color: active ? 'var(--gold)' : 'var(--text-3)',
                minWidth: '22px',
                transition: 'color 0.15s ease',
              }}>
                {num}
              </span>
              <span style={{
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.82rem',
                fontWeight: active ? 500 : 400,
                color: active ? 'var(--text)' : 'var(--text-2)',
                transition: 'color 0.15s ease',
              }}>
                {label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{
        padding: '16px 24px 24px',
        borderTop: '1px solid var(--border)',
      }}>
        <div style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.58rem',
          color: 'var(--text-3)',
          letterSpacing: '0.06em',
          lineHeight: 1.6,
        }}>
          <div>ilvecho.com</div>
          <div style={{ marginTop: '2px', opacity: 0.7 }}>
            Citizen of the world
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile top bar */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '52px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          zIndex: 50,
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: '1.3rem',
              fontWeight: 700,
              color: 'var(--text)',
              letterSpacing: '-0.02em',
            }}>
              MT
            </span>
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              color: 'var(--text-2)',
            }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              /* X icon */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            ) : (
              /* Hamburger icon */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="7" x2="21" y2="7"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="17" x2="21" y2="17"/>
              </svg>
            )}
          </button>
        </div>

        {/* Mobile overlay menu */}
        {mobileOpen && (
          <div style={{
            position: 'fixed',
            top: '52px',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--surface)',
            borderTop: '1px solid var(--border)',
            zIndex: 49,
            overflowY: 'auto',
          }}>
            <nav style={{ padding: '16px 0' }}>
              {NAV_ITEMS.map(({ num, label, href }) => {
                const active = isActive(href);
                return (
                  <Link
                    key={href}
                    to={href}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '14px 24px',
                      textDecoration: 'none',
                      background: active ? 'var(--surface-2)' : 'transparent',
                      borderLeft: active ? '2px solid var(--gold)' : '2px solid transparent',
                    }}
                  >
                    <span style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.7rem',
                      color: active ? 'var(--gold)' : 'var(--text-3)',
                    }}>
                      {num}
                    </span>
                    <span style={{
                      fontSize: '1rem',
                      fontWeight: active ? 600 : 400,
                      color: active ? 'var(--text)' : 'var(--text-2)',
                    }}>
                      {label}
                    </span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </>
    );
  }

  return <SidebarContent />;
}
